import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { AlertTriangleIcon } from "./icons.tsx";

interface FieldShellProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optionalLabel?: string;
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function FieldShell({ label, hint, error, optionalLabel, className, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={["field", className ?? ""].join(" ")}>
      <label className="field-label" htmlFor={id}>
        {label}
        {optionalLabel ? <span className="optional"> ({optionalLabel})</span> : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId}>
          <AlertTriangleIcon size={14} />
          {error}
        </p>
      ) : null}
    </div>
  );
}

type BaseProps = { label: ReactNode; hint?: ReactNode; error?: string | null; optionalLabel?: string; fieldClassName?: string };

export const TextField = forwardRef<HTMLInputElement, BaseProps & InputHTMLAttributes<HTMLInputElement>>(function TextField(
  { label, hint, error, optionalLabel, fieldClassName, className, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} {...(optionalLabel ? { optionalLabel } : {})} {...(fieldClassName ? { className: fieldClassName } : {})}>
      {({ id, describedBy, invalid }) => (
        <input
          ref={ref}
          id={id}
          className={["input", className ?? ""].join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

/**
 * Write-only secret input (RF-CFG-02, §10.1): always masked, never pre-filled with a stored
 * value, never autocompleted, and no reveal toggle. After saving, only a masked state is shown.
 */
export const SecretField = forwardRef<
  HTMLInputElement,
  BaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "defaultValue"> & { storedState?: string }
>(function SecretField({ label, hint, error, optionalLabel, fieldClassName, className, storedState, ...rest }, ref) {
  return (
    <FieldShell
      label={label}
      hint={
        <>
          {storedState ? <span>{storedState} · </span> : null}
          {hint}
        </>
      }
      error={error ?? null}
      {...(optionalLabel ? { optionalLabel } : {})}
      {...(fieldClassName ? { className: fieldClassName } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <input
          ref={ref}
          id={id}
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          data-secret="true"
          className={["input", className ?? ""].join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

export const TextAreaField = forwardRef<HTMLTextAreaElement, BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextAreaField(
  { label, hint, error, optionalLabel, fieldClassName, className, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} {...(optionalLabel ? { optionalLabel } : {})} {...(fieldClassName ? { className: fieldClassName } : {})}>
      {({ id, describedBy, invalid }) => (
        <textarea
          ref={ref}
          id={id}
          className={["textarea", className ?? ""].join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

export const SelectField = forwardRef<HTMLSelectElement, BaseProps & SelectHTMLAttributes<HTMLSelectElement>>(function SelectField(
  { label, hint, error, optionalLabel, fieldClassName, className, children, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} {...(optionalLabel ? { optionalLabel } : {})} {...(fieldClassName ? { className: fieldClassName } : {})}>
      {({ id, describedBy, invalid }) => (
        <select
          ref={ref}
          id={id}
          className={["select", className ?? ""].join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
});

/**
 * Checkbox field: the box sits beside the first line of its label and the hint is indented
 * under the label text (`.check-field`). Use inside a form grid/stack, or full width.
 */
export const CheckboxField = forwardRef<
  HTMLInputElement,
  { label: ReactNode; hint?: ReactNode; error?: string | null; fieldClassName?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, "type">
>(function CheckboxField({ label, hint, error, fieldClassName, className, ...rest }, ref) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={["check-field", fieldClassName ?? ""].join(" ")}>
      <input ref={ref} id={id} type="checkbox" className={className} aria-describedby={describedBy} aria-invalid={Boolean(error) || undefined} {...rest} />
      <label htmlFor={id}>{label}</label>
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId}>
          <AlertTriangleIcon size={14} />
          {error}
        </p>
      ) : null}
    </div>
  );
});
